import { Map, View, Feature } from 'ol';
import { Tile as TileLayer, Group as LayerGroup } from 'ol/layer';
import { XYZ } from 'ol/source';
import { defaults as defaultControls, ScaleLine } from 'ol/control';
import { fromLonLat, transform } from 'ol/proj';
import { Polygon } from 'ol/geom';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import { Style, Fill, Stroke } from 'ol/style';
import { apply } from 'ol-mapbox-style';
import { BaseMapToggle } from '../shared/nhc-modules/basemap-toggle';
import './style.css';

/**
 * These variables are set in the including HTML file:
 *   inundationDir, jsFileNoMask, jsFileMask, downloads
 * 
 * These variables are set in the included _inundation.js file:
 *   year1, maptitle, stormNameAdv1, nametitle1, stm1, stmid, adv1, 
 *   headerDateString1, mapCenterX1, mapCenterY1, displayMap, mapStatus,
 *   mapLegend, mapWindow, textA, textB, summary1, descriptionA1, 
 *   descriptionB1, bounds1, levels1
 * 
 * These variables are set in the included _tidalmask.js file:
 *   stm2, bounds2, levels2
 * 
 * These variables are set in the included polygon_windows8.js file:
 *  regions
 */

/**
 * Set up the map center based on the provided coordinates
 */
const mapCenter = fromLonLat([mapCenterX1, mapCenterY1]);

/**
 * Set up the scale bar
 */
const scaleBar = new ScaleLine({
  units: "imperial",
  bar: true
});

/**
 * Set up the noMask layer
 * We use tileUrlFunction to check if the tile exists before trying to load it
 */
const noMaskLayer = new TileLayer({
  source: new XYZ({
    tileUrlFunction: getNoMaskTileUrl,
    projection: "EPSG:3857",
    tileSize: [256, 256],
    maxZoom: 12,
    minZoom: 6,
  }),
});

/**
 * Set up the tidalMask layer
 * We use tileUrlFunction to check if the tile exists before trying to load it
 */
const tidalMaskLayer = new TileLayer({
  source: new XYZ({
    tileUrlFunction: getTidalMaskTileUrl,
    projection: "EPSG:3857",
    tileSize: [256, 256],
    maxZoom: 12,
    minZoom: 6,
  }),
  visible: false
});

/**
 * Add map geometry "window" to the map
 */
const polyLayer = new VectorLayer({
  source: new VectorSource({
    features: [
      new Feature({
        geometry: new Polygon(
          getCoordinates()
        ),
        style: new Style({
          fill: new Fill({
            color: 'rgba(0, 0, 0, 0.5)',
          }),
          stroke: new Stroke({
            color: '#000',
            width: 3,
          }),
        }),
      }),
    ],
  }),
});

/**
 * Create an ESRI base map layer group (Dark Gray)
 */
const grayLayer = new LayerGroup();
const apiKey = import.meta.env.VITE_ESRI_API_KEY;
let basemapId = "arcgis/dark-gray";
let basemapURL = `https://basemapstyles-api.arcgis.com/arcgis/rest/services/styles/v2/styles/${basemapId}?token=${apiKey}`;
apply(grayLayer, basemapURL);

/**
 * Create an ESRI base map layer group (Imagery)
 */
const hybridLayer = new LayerGroup();
basemapId = "arcgis/imagery";
basemapURL = `https://basemapstyles-api.arcgis.com/arcgis/rest/services/styles/v2/styles/${basemapId}?token=${apiKey}`;
apply(hybridLayer, basemapURL);

/**
 * Define the base map toggle control
 */
const baseMapToggle = new BaseMapToggle({
  startLayer: grayLayer,
  startLayerName: 'Dark Gray Canvas',
  startLayerImage: '/images/dark-gray.jpg',
  nextLayer: hybridLayer,
  nextLayerName: 'Imagery with Labels',
  nextLayerImage: '/images/hybrid.jpg'
});

/**
 * Create the map
 */
const map = new Map({
  controls: defaultControls().extend([
    scaleBar,
    baseMapToggle
  ]),
  layers: [
    grayLayer,
    noMaskLayer,
    tidalMaskLayer,
    polyLayer
  ],
  target: document.getElementById('map'),
  view: new View({
    center: mapCenter,
    zoom: 6,
    maxZoom: 12,
    minZoom: 6
  })
});

/**
 * Once the DOM is loaded and the page is ready...
 */
$(function() {
  /* Display header information */
  displayHeader();

  /* Apply the map overlay */
  applyOverlay();

  /* Display the legend */
  displayLegend();
  displayLinks();

  /* Leave the overlay or show the dialog box? */
  initializeMapOverlays();

  // Remove the radio buttons from the control group
  $("input[type=radio]").checkboxradio({
    icon: false
  });
  
  $("#radio").on("change", function(t) {
    var maskIsChecked = $("#radio2").is(":checked");
    if (displayMap) {
      swopDataLayers(maskIsChecked);
    }
  });

});


// ================================================
// Support functions
// ================================================

/**
 * Get the URL for the noMask tile & return null if it doesn't exist.
 * We use this function to check if the tile exists before trying to load it
 * so we can avoid 404 errors.
 * @param {Array<number>} coordinate - The tile coordinate (z, x, y)
 * @returns {string} The URL for the tile
 */
function getNoMaskTileUrl(coordinate) {
  const level = coordinate[0];
  const col = coordinate[1];
  const row = coordinate[2];
  // First check if the coordinates make sense
  if (
    level < 6 || 
    level > 12 || 
    bounds1[level][0][0] > col || 
    col > bounds1[level][0][1] ||
    bounds1[level][1][0] > row || 
    row > bounds1[level][1][1]
  ) {
    return null;
  }
  // Then check if the tile exists
  if (noMaskTileExists(level, col,row) === false) {
    return null;
  }
  const noMaskPath = `/storm_graphics/${stmid}/inundation/nomask/L${level}`;
  const noMaskFile = `${stm1}_${col}i_${row}j.png`;
  return `${noMaskPath}/${noMaskFile}`;
}

/**
 * Check the levels1 object (noMask) to see if the tile exists
 * @param {integer} level 
 * @param {integer} col 
 * @param {integer} row 
 * @returns {boolean} Whether the tile exists or not
 */
function noMaskTileExists(level, col, row) {
  const obj = levels1[String(level)];
  const tilesPerRow = (obj.xMax - obj.xMin) + 1;
  const iTile = (col - obj.xMin) + ((row - obj.yMin) * tilesPerRow);
  return Boolean(obj.tiles[iTile]);
}

/**
 * Get the URL for the tidalMask tile & return null if it doesn't exist.
 * We use this function to check if the tile exists before trying to load it
 * so we can avoid 404 errors.
 * @param {Array<number>} coordinate - The tile coordinate (z, x, y)
 * @returns {string} The URL for the tile
 */
function getTidalMaskTileUrl(coordinate) {
  const level = coordinate[0];
  const col = coordinate[1];
  const row = coordinate[2];
  // First check if the coordinates make sense
  if (
    level < 6 || 
    level > 12 || 
    bounds2[level][0][0] > col || 
    col > bounds2[level][0][1] ||
    bounds2[level][1][0] > row || 
    row > bounds2[level][1][1]
  ) {
    return null;
  }
  // Then check if the tile exists
  if (tidalMaskTileExists(level, col,row) === false) {
    return null;
  }
  const tidalMaskPath = `/storm_graphics/${stmid}/inundation/tidalmask/L${level}`;
  const tidalMaskFile = `${stm2}_${col}i_${row}j.png`;
  return `${tidalMaskPath}/${tidalMaskFile}`;
}

/**
 * Check the levels1 object (tidalMask) to see if the tile exists
 * @param {integer} level 
 * @param {integer} col 
 * @param {integer} row 
 * @returns {boolean} Whether the tile exists or not
 */
function tidalMaskTileExists(level, col, row) {
  const obj = levels2[String(level)];
  const tilesPerRow = (obj.xMax - obj.xMin) + 1;
  const iTile = (col - obj.xMin) + ((row - obj.yMin) * tilesPerRow);
  return Boolean(obj.tiles[iTile]);
}


/**
 * Calculate the coordinates for the polygon
 * This uses the regions object from the polygon_windows8.js file
 * and the mapWindow variable from the _inundation.js file
 * @returns {Array<Array<Array<number>>>} The transformed coordinates
 */
function getCoordinates() {
  const region = mapWindow;
  const rings = regions[region].rings;
  const transformedCoordinates = [];
  for (let i = 0; i < rings.length; i++) {
    const ring = rings[i];
    const transformedRing = ring.map(
      coord => transform(coord, 'EPSG:4326', 'EPSG:3857')
    );
    transformedCoordinates.push(transformedRing);
  }
  return transformedCoordinates;
}

/**
 * Display the header information
 */
function displayHeader() {
  $('p.title').html(`${maptitle}<br>${stormNameAdv1}<br>${headerDateString1}`);
}

/**
 * Display the legend
 */
function displayLegend() {
  // set which .png legend to apply to map
  $('#legend-image').html(`<img src="/templates/storm/inundation/${mapLegend}">`);
  // set which additional text gets placed next to legend and below map options
  $('#legend-notes').html(`<p>${textA}</p>`);
}
/**
 * Display the download links
 */
function displayLinks() {
  $('#inundation-link').html(`<center><a href="${downloads}/${fileRoot}_${adv1}_inundation.zip">Inundation Layer Only</a></center>`);
  $('#tidalmask-link').html(`<center><a href="${downloads}/${fileRoot}_${adv1}_tidalmask.zip">Inundation with Intertidal Layer</a></center>`);
}

/**
 * Initialize the map overlays
 */
function initializeMapOverlays(){
  addRadio();
  if (displayMap) {
    removeOverlay();
    $('#jq-dialog').html(`<p>${descriptionA1}</p>`);
    $('#jq-dialog').append(`<p>${descriptionB1}</p>`);
    createDialog();
  }
  else {
    $('#jq-dialog').hide();
    $('#overlay').html(`<h1>${mapStatus}</h1>`);
  }
}

/**
 * Enable or disable the radio buttons
 */
function enableRadio(){
  $("#radio").controlgroup("option", "disabled", false);
}

/**
 * Disable the radio buttons
 */
function disableRadio(){
  $("#radio").controlgroup("option", "disabled", true);
}

/**
 * Add radio buttons to the control group
 */
function addRadio() {
  $("#radio").controlgroup();
  disableRadio();
}

/**
 * Make opacity slider
 */
function makeSlider() {
  $('#opacity-slider').slider({
    min: 15,
    max: 100,
    value: 100,
    step: 1,
    range: false,
    slide: function(event, ui) {
      noMaskLayer.setOpacity(ui.value/100);
      tidalMaskLayer.setOpacity(ui.value/100);
    } 
  });    
}
     
/**
 * Create dialog
 */
function createDialog() {
  $("#jq-dialog").dialog({
    //appendTo: 'body',
    height: 450,
    width: 350,
    modal: false,
    resizable: false,
    open: function(event, ui) {
      $(".ui-dialog-titlebar-close").hide();
    },
    draggable: true,
    buttons: {
      "Continue": function() { 
        $( this ).dialog("close");
        swopDataLayers(true); // default is to show the tidal mask layer
        enableRadio();
        makeSlider();
      },
      "Return to homepage": function() {
        document.location.href='http://www.nhc.noaa.gov';
      }
    }
  });
  $('#jq-dialog').parent().position({ my: 'center', at: 'center', of: '#map' });
  $('.ui-dialog').draggable( "option", "containment", "#map" );
}

/**
 * Switch between the inundation (noMask) and tidal mask (tidalMask) layers
 * @param {boolean} maskIsChecked - True if the tidal mask layer is checked
 */
function swopDataLayers(maskIsChecked) {
  if (maskIsChecked) {
    noMaskLayer.setVisible(false);
    tidalMaskLayer.setVisible(true);
  }
  else {
    tidalMaskLayer.setVisible(false);
    noMaskLayer.setVisible(true);
  }
}

/**
 * Get div height, width and positions of the map for the overlay
 */
function applyOverlay(){
  if (!displayMap){
    $('#overlay').attr('class', 'bad-overlay');
  }
  else {
    $('#overlay').attr('class', 'good-overlay');
  }
}

/**
 * Remove the overlay
 */
function removeOverlay(){
  var element = document.getElementById("overlay");
  element.parentNode.removeChild(element);
}
